// frontend/src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { CssBaseline, Box } from "@mui/material";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  responsiveFontSizes,
} from "@mui/material/styles";
import BottomNavigation from "./components/BottomNavigation";
import HomePage from "./pages/HomePage";
import ScanPage from "./pages/ScanPage";
import SettingsPage from "./pages/SettingsPage";
import NewsPage from "./pages/NewsPage";
import MarketplacePage from "./pages/MarketplacePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import CustomerLoginPage from "./pages/CustomerLoginPage";
import CustomerDashboardPage from "./pages/CustomerDashboardPage";
import ClientManagementPage from "./pages/ClientManagementPage";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import PrivateRoute from "./routes/PrivateRoute";
import BackendManagement from "./components/admin/BackendManagement";
import ClientManagement from "./components/admin/ClientManagement";
import TopBar from "./components/TopBar";
import AdminDashboard from "./pages/AdminDashboard";
import SexRatioAnalytics from "./components/SexRatioAnalytics";
import BottomBar from "./components/BottomBar";

// App content with theme context
const AppContent = () => {
  const { darkMode } = useTheme();

  // Create theme based on dark mode preference
  let theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: "#184d18", // Dark green from the Ranko logo
        light: "#2e7d32",
        dark: "#103010",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#a6ed44", // Light green from the Ranko logo leaves
        light: "#c8ff78",
        dark: "#81c926",
        contrastText: "#000000",
      },
    },
    typography: {
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  });

  // Make typography responsive
  theme = responsiveFontSizes(theme);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          <TopBar />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: { xs: 1, md: 3 },
              mt: 8,
              mb: { xs: 7, md: 7 },
            }}
          >
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/customer-login" element={<CustomerLoginPage />} />
              <Route
                path="/customer-dashboard"
                element={
                  <PrivateRoute requiredPermission="is_client">
                    <CustomerDashboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/scan"
                element={
                  <PrivateRoute requiredPermission="can_scan">
                    <ScanPage />
                  </PrivateRoute>
                }
              />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route
                path="/admin"
                element={
                  <PrivateRoute requiredPermission="is_staff">
                    <AdminPage />
                  </PrivateRoute>
                }
              />
              <Route path="/admin/clients" element={<ClientManagement />} />
              <Route
                path="/backend-management"
                element={
                  <PrivateRoute requiredPermission="is_superuser">
                    <BackendManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <PrivateRoute requiredPermission="is_staff">
                    <AdminDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/client-management"
                element={
                  <PrivateRoute adminOnly={true}>
                    <ClientManagementPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <PrivateRoute requiredPermission="can_view_analytics">
                    <SexRatioAnalytics />
                  </PrivateRoute>
                }
              />
              <Route
                path="/sex-ratio-analytics"
                element={
                  <PrivateRoute requiredPermission="can_view_analytics">
                    <SexRatioAnalytics />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Box>
          <BottomBar />
        </div>
      </Router>
    </MuiThemeProvider>
  );
};

// Main App component
const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
